/**
 * Payment Callback Page — after PayChangu redirect (?tx_ref=...)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Check, X, RefreshCw, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { classifyError } from '../lib/apiError';
import { getApiUrl, PAYMENT_POLL_TIMEOUT_MS } from '../config';
import ContactSupportButtons from '../components/ContactSupportButtons';

const AUTO_HOME_SECONDS = 12;

const PaymentCallbackPage = () => {
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);
  const [txId, setTxId] = useState(() => new URLSearchParams(window.location.search).get('tx'));
  const [errorDetail, setErrorDetail] = useState(null);
  const [homeCountdown, setHomeCountdown] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const statusUrl = useCallback((paymentTxId, txRef) => {
    const api = getApiUrl();
    if (txRef) {
      return `${api}/transactions/status/ref/${encodeURIComponent(txRef)}?sync=true`;
    }
    if (paymentTxId) {
      return `${api}/transactions/status/${paymentTxId}?sync=true`;
    }
    return null;
  }, []);

  const checkPaymentStatus = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const paymentTxId = params.get('tx');
    let txRef = params.get('tx_ref');
    const payStatus = params.get('status');
    if (!txRef) {
      txRef = sessionStorage.getItem('pending_payment_ref') || sessionStorage.getItem('pending_wallet_ref');
    }
    setErrorDetail(null);
    setStatus('checking');

    // Wallet top-up return (WAL… reference)
    if (txRef?.startsWith('WAL')) {
      try {
        const token = localStorage.getItem('auth_token');
        const api = getApiUrl();
        const response = await axios.get(
          `${api}/wallet/add-funds/status/ref/${encodeURIComponent(txRef)}?sync=true`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 45000 }
        );
        if (response.data?.status === 'completed' || response.data?.success) {
          setTransaction({ ...response.data, reference: txRef, type: 'wallet_topup' });
          setStatus('wallet_topup_success');
          sessionStorage.removeItem('pending_wallet_ref');
          return;
        }
        setStatus('processing');
        return;
      } catch (err) {
        setStatus('error');
        setErrorDetail(classifyError(err).message);
        return;
      }
    }

    const url = statusUrl(paymentTxId, txRef);
    if (!url) {
      if (payStatus === 'successful' || payStatus === 'success') setStatus('success');
      else if (payStatus === 'cancelled' || payStatus === 'canceled') setStatus('failed');
      else setStatus('error');
      return;
    }

    try {
      let attempts = 0;
      const pollStatus = async () => {
        try {
          const response = await axios.get(url, { timeout: 45000 });
          const tx = response.data;
          if (tx.id) setTxId(tx.id);
          if (tx.status === 'success' && tx.token) {
            setTransaction(tx);
            setStatus('success');
            sessionStorage.removeItem('pending_payment_ref');
            return true;
          }
          if (tx.status === 'refunded') {
            setTransaction(tx);
            setStatus('refunded');
            if (tx.fulfillment_error) setErrorDetail(tx.fulfillment_error);
            return true;
          }
          if (tx.status === 'fulfillment_failed') {
            setTransaction(tx);
            setStatus('fulfillment_failed');
            if (tx.fulfillment_error) setErrorDetail(tx.fulfillment_error);
            return true;
          }
          if (tx.status === 'processing' || tx.payment_settling) {
            setStatus('processing');
            if (tx.fulfillment_error) setErrorDetail(tx.fulfillment_error);
          }
          if (tx.status === 'failed' || tx.status === 'expired') {
            setStatus('failed');
            return true;
          }
          const authToken = localStorage.getItem('auth_token');
          const activeTxId = tx.id || paymentTxId;
          if (
            authToken &&
            attempts >= 2 &&
            (tx.status === 'pending' ||
              tx.status === 'processing' ||
              tx.status === 'fulfillment_failed')
          ) {
            try {
              if (tx.status === 'fulfillment_failed' || tx.status === 'processing') {
                if (!activeTxId) return false;
                const fulfillRes = await axios.post(
                  `${getApiUrl()}/payments/fulfill/${activeTxId}`,
                  {},
                  { headers: { Authorization: `Bearer ${authToken}` } }
                );
                const fulfilled = fulfillRes.data?.transaction;
                if (fulfilled?.status === 'success' && fulfilled?.token) {
                  setTransaction(fulfilled);
                  setStatus('success');
                  return true;
                }
              } else if (activeTxId) {
                const verifyRes = await axios.post(
                  `${getApiUrl()}/payments/verify/${activeTxId}`,
                  {},
                  { headers: { Authorization: `Bearer ${authToken}` } }
                );
                const verified = verifyRes.data?.transaction;
                if (verified?.status === 'success' && verified?.token) {
                  setTransaction(verified);
                  setStatus('success');
                  return true;
                }
                if (verified?.status === 'fulfillment_failed') {
                  setTransaction(verified);
                  setStatus('fulfillment_failed');
                  if (verified.fulfillment_error) setErrorDetail(verified.fulfillment_error);
                  return true;
                }
                if (verified?.status === 'failed') {
                  setStatus('failed');
                  return true;
                }
              }
            } catch {
              /* fall through */
            }
          }
          if (attempts >= 300) {
            setStatus(tx.status === 'pending' ? 'pending' : 'error');
            if (!tx.id) {
              setErrorDetail('Could not confirm payment status. Check History or retry below.');
            }
            return true;
          }
          attempts++;
          return false;
        } catch (error) {
          const msg = classifyError(error).message;
          if (attempts >= 2) {
            setStatus('error');
            const txRef = new URLSearchParams(window.location.search).get('tx_ref');
            const devLink = txRef
              ? `http://localhost:3000/payment/callback?tx_ref=${encodeURIComponent(txRef)}`
              : 'http://localhost:3000/payment/callback';
            setErrorDetail(
              `${msg}. API: ${getApiUrl()}. Open the app on port 3000: ${devLink}`
            );
            return true;
          }
          attempts++;
          return false;
        }
      };

      const done = await pollStatus();
      if (!done) {
        const interval = setInterval(async () => {
          if (await pollStatus()) clearInterval(interval);
        }, 2000);
        setTimeout(() => clearInterval(interval), PAYMENT_POLL_TIMEOUT_MS);
      }
    } catch (error) {
      setStatus('error');
      setErrorDetail(classifyError(error).message);
    }
  }, [statusUrl]);

  const requestRefund = async () => {
    const authToken = localStorage.getItem('auth_token');
    const activeTxId = transaction?.id || txId;
    if (!authToken || !activeTxId) {
      toast.error('Sign in to request a refund');
      return;
    }
    try {
      const response = await axios.post(
        `${getApiUrl()}/payments/refund/${activeTxId}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const tx = response.data?.transaction;
      setTransaction(tx || transaction);
      setStatus('refunded');
      toast.success(response.data?.message || 'Refund initiated to your original payment method');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refund failed');
    }
  };

  useEffect(() => {
    if (status !== 'success' || !transaction?.token) {
      setHomeCountdown(null);
      return undefined;
    }
    setHomeCountdown(AUTO_HOME_SECONDS);
    const interval = setInterval(() => {
      setHomeCountdown((prev) => (prev == null || prev <= 1 ? null : prev - 1));
    }, 1000);
    const timeout = setTimeout(() => {
      navigate(user ? '/' : '/login', { replace: true });
    }, AUTO_HOME_SECONDS * 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [status, transaction?.token, user, navigate]);

  useEffect(() => {
    const { hostname, port, pathname, search, protocol } = window.location;
    const effectivePort = port || (protocol === 'https:' ? '443' : '80');
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
    const onBareLocalPort =
      isLocalHost && (effectivePort === '80' || effectivePort === '443' || port === '');

    // Bare http://localhost (port 80) has no CRA dev server — send to :3000.
    if (onBareLocalPort && pathname.includes('/payment/callback')) {
      window.location.replace(`http://${hostname}:3000${pathname}${search}`);
      return;
    }

    checkPaymentStatus();
  }, [checkPaymentStatus]);

  const shell = (children) => (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: '#f4f4f5', color: '#18181b' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center space-y-6 p-6 rounded-2xl bg-white shadow-lg border border-zinc-200"
      >
        {children}
      </motion.div>
    </div>
  );

  return shell(
    <>
      {status === 'checking' && (
        <>
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
          <h2 className="text-xl font-semibold">Verifying Payment…</h2>
          <p className="text-sm text-zinc-500">Reference: {new URLSearchParams(window.location.search).get('tx_ref') || '—'}</p>
        </>
      )}

      {status === 'success' && (
        <>
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold">Payment Successful!</h2>
          {transaction?.customer_name && (
            <p className="text-sm text-zinc-600">
              Account: <span className="font-medium text-zinc-900">{transaction.customer_name}</span>
            </p>
          )}
          {transaction?.token && (
            <div className="rounded-2xl p-6 space-y-4 bg-emerald-50 border border-emerald-200">
              <p className="text-sm text-zinc-600">Your Token</p>
              <p className="font-mono text-lg font-bold break-all text-emerald-800">{transaction.token}</p>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(transaction.token);
                  toast.success('Token copied!');
                }}
                className="w-full py-3 bg-zinc-100 rounded-xl font-medium flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" /> Copy Token
              </button>
            </div>
          )}
          {homeCountdown != null && (
            <p className="text-sm text-zinc-500">
              Returning to {user ? 'dashboard' : 'login'} in {homeCountdown}s…
            </p>
          )}
          <button
            type="button"
            onClick={() => navigate(user ? '/' : '/login', { replace: true })}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            {user ? 'Go to Dashboard now' : 'Login now'}
          </button>
        </>
      )}

      {status === 'wallet_topup_success' && (
        <>
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold">Wallet Top-Up Successful!</h2>
          <p className="text-zinc-600">
            MWK {(transaction?.amount || 0).toLocaleString()} added to your SmartPay wallet.
            {transaction?.new_balance != null && (
              <> New balance: MWK {Number(transaction.new_balance).toLocaleString()}.</>
            )}
          </p>
          <button
            type="button"
            onClick={() => navigate(user ? '/wallet' : '/login', { replace: true })}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            {user ? 'View Wallet' : 'Login'}
          </button>
        </>
      )}

      {status === 'failed' && (
        <>
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <X className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold">Payment Failed</h2>
          <button
            type="button"
            onClick={() => navigate(user ? '/buy' : '/login')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            Try Again
          </button>
        </>
      )}

      {status === 'processing' && (
        <>
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
          <h2 className="text-2xl font-bold">Issuing Your Token…</h2>
          {transaction?.customer_name && (
            <p className="text-sm text-zinc-600">
              Account holder: <span className="font-medium">{transaction.customer_name}</span>
            </p>
          )}
          <p className="text-sm text-zinc-600">
            Payment confirmed with PayChangu. We are issuing your utility token via bill pay (utility amount only).
            Your platform fee stays in the merchant wallet. Payout to your bank follows PayChangu&apos;s settlement schedule.
          </p>
          {errorDetail && (
            <p className="text-xs text-amber-800 bg-amber-50 p-3 rounded-lg break-words text-left">{errorDetail}</p>
          )}
          <button
            type="button"
            onClick={checkPaymentStatus}
            className="px-6 py-3 bg-zinc-100 rounded-xl font-medium flex items-center justify-center gap-2 mx-auto"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </>
      )}

      {status === 'fulfillment_failed' && (
        <>
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
            <X className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold">Token Not Issued</h2>
          {transaction?.customer_name && (
            <p className="text-sm text-zinc-600">
              Account holder: <span className="font-medium">{transaction.customer_name}</span>
            </p>
          )}
          <p className="text-sm text-zinc-600">
            Payment was received but your utility token could not be delivered. You can retry below
            or request a refund to your original payment method.
          </p>
          {errorDetail && (
            <p className="text-xs text-amber-800 bg-amber-50 p-3 rounded-lg break-words">{errorDetail}</p>
          )}
          <button
            type="button"
            onClick={checkPaymentStatus}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 mx-auto"
          >
            <RefreshCw className="w-4 h-4" /> Retry token delivery
          </button>
          <button
            type="button"
            onClick={requestRefund}
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium"
          >
            Refund to original payment method
          </button>
          <button
            type="button"
            onClick={() => navigate(user ? '/history' : '/login')}
            className="px-6 py-3 bg-zinc-100 rounded-xl font-medium"
          >
            View History
          </button>
          <div className="pt-4 border-t border-zinc-200 text-left max-w-sm mx-auto">
            <ContactSupportButtons compact />
          </div>
        </>
      )}

      {status === 'refunded' && (
        <>
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold">Payment Refunded</h2>
          <p className="text-sm text-zinc-600">
            The full amount is being returned to your original payment method. No utility token was issued.
          </p>
          <button
            type="button"
            onClick={() => navigate(user ? '/history' : '/login')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            View History
          </button>
        </>
      )}

      {status === 'pending' && (
        <>
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
            <RefreshCw className="w-10 h-10 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold">Payment Pending</h2>
          <p className="text-sm text-zinc-600">If you already paid, sync with PayChangu below.</p>
          <button
            type="button"
            onClick={checkPaymentStatus}
            className="px-6 py-3 bg-zinc-100 rounded-xl font-medium flex items-center justify-center gap-2 mx-auto"
          >
            <RefreshCw className="w-4 h-4" /> Check again
          </button>
          <button
            type="button"
            onClick={() => navigate(user ? '/history' : '/login')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            View History
          </button>
        </>
      )}

      {status === 'error' && (
        <>
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <X className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold">Could Not Load Payment Status</h2>
          {errorDetail && (
            <p className="text-xs text-red-800 bg-red-50 p-3 rounded-lg break-words text-left">{errorDetail}</p>
          )}
          <button
            type="button"
            onClick={checkPaymentStatus}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => navigate(user ? '/' : '/login')}
            className="px-6 py-3 bg-zinc-100 rounded-xl font-medium"
          >
            Go Back
          </button>
        </>
      )}
    </>
  );
};

export default PaymentCallbackPage;
